/**
 * RegionTilesRow.tsx — PPP Stream B, DLD §B6
 *
 * Data-driven region tiles. Accepts any number of RegionStat entries
 * and renders one tile per region. New regions auto-appear without code
 * changes. Layout: auto-fill grid matching proto `.tiles`.
 *
 * Phase TTT: clicking a tile toggles map highlight via onSelectRegion; no
 * longer navigates to /entities. Active tile shows aria-pressed + ring.
 */
import { useTranslation } from "react-i18next";
import type { Region } from "../lib/types";
import { getRegionColor } from "../lib/regionColors";

export interface RegionStat {
  region: Region;
  entities: number;
  countries: number;
}

interface RegionTilesRowProps {
  stats: RegionStat[];
  selectedRegion: Region | null;
  onSelectRegion: (r: Region | null) => void;
}

export function RegionTilesRow({ stats, selectedRegion, onSelectRegion }: RegionTilesRowProps): JSX.Element {
  const { t } = useTranslation("entities");
  const { t: tCommon } = useTranslation("common");

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 gap-[13px] mb-5"
      role="group"
      aria-label="Filter entities by region"
    >
      {stats.map(({ region, entities, countries }) => {
        const regionLabel = tCommon(`region_${region}`, region);
        const isActive = selectedRegion === region;
        const colorHex = getRegionColor(region, "hex");

        return (
          <button
            key={region}
            type="button"
            aria-label={`Filter entities by ${regionLabel}`}
            aria-pressed={isActive}
            onClick={() => onSelectRegion(isActive ? null : region)}
            className={[
              "relative overflow-hidden flex flex-col p-[15px_16px] text-left w-full",
              "bg-surface border rounded-[11px] shadow-card",
              "transition-[box-shadow,transform,border-color] duration-[140ms]",
              "min-h-[44px]",
              isActive
                ? "border-accent ring-2 ring-accent/20 -translate-y-px shadow-card-hov"
                : "border-border hover:shadow-card-hov hover:-translate-y-px",
            ].join(" ")}
          >
            {/* Left accent bar */}
            <span
              aria-hidden
              className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[11px]"
              style={{ background: colorHex }}
            />

            {/* Region label row */}
            <div className="flex items-center gap-[7px] text-[10.5px] font-bold tracking-[.05em] uppercase text-muted">
              <span
                aria-hidden
                className="w-2 h-2 rounded-full shrink-0"
                style={{ background: colorHex }}
              />
              <span>{regionLabel}</span>
            </div>

            {/* Big number */}
            <div className="mt-[7px] font-display text-[28px] font-semibold leading-[1.1] text-text tabular-nums">
              {entities}
              <small className="font-sans text-[12px] font-medium text-muted ml-[5px]">
                {t("tile_entities")}
              </small>
            </div>

            {/* Country count */}
            <div className="text-[11.5px] text-dim mt-0.5">
              {countries} {t("tile_countries")}
            </div>
          </button>
        );
      })}
    </div>
  );
}
